<template>
  <div>
    <div class="my-4">
      <span class="header-icon">
        <font-awesome-icon :icon="['fas', 'list']" />
      </span>
      <h2>{{ $t('menu.settings.users.listActions') }}</h2>
    </div>

    <div class="header mb-3">
      <div class="search">
        <input
          type="text"
          class="form-control"
          id="input-answer"
          :placeholder="`${this.$t('menu.settings.users.placeholderSearch')}`"
          v-model="filter"
          @keyup="handleFilter"
        />
        <button @click="cleanFilter" class="btn btn-outline-primary">
          {{ $t('menu.settings.users.clear') }}
        </button>

        <div style="margin-left: 50px; width: 100%;">
          <b-form-select
            style="height: 35px;"
            class="custom-select"
            v-model="groupSelected"
            :options="optionsGroups"
            @change="groupFilter(groupSelected)"
            size="sm"
          ></b-form-select>
        </div>
      </div>

      <b-form-checkbox @change="editAllActions($event)" switch>
        {{ $t('menu.settings.users.selectAll') }}
      </b-form-checkbox>
    </div>

    <div>
      <b-table
        striped
        :items="actionItems"
        :fields="actionFields"
        :busy="loading"
      >
        <template v-slot:table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner type="grow" class="align-middle"></b-spinner>
          </div>
        </template>

        <template v-slot:cell(createdAt)="row">
          <p>{{ formatDate(row.item.createdAt) }}</p>
        </template>
        <template v-slot:cell(groupId)="row">
          <span v-if="row.item.groupId.length > 1" style="display: flex;">
            {{ row.item.groupId[0].name }}
            <div class="groupListStyle">
              + {{ row.item.groupId.length - 1 }}
            </div>
          </span>
          <span v-else-if="row.item.groupId.length === 1">
            {{ row.item.groupId[0].name }}
          </span>
          <span v-else>
            -
          </span>
        </template>
        <template v-slot:cell(status)="row">
          <b-form-checkbox
            @change="editActionStatus(row.item.status)"
            v-model="row.item.status.active"
            switch
          >
          </b-form-checkbox>
        </template>
      </b-table>
      <div class="pagination-area-answers">
        <paginate
          v-model="page"
          :pageCount="Math.ceil(total / this.limit)"
          :clickHandler="getActions"
          :prevText="'‹'"
          :nextText="'›'"
          :activeClass="'active'"
          :containerClass="'pagination b-pagination'"
          :pageClass="'page-item'"
        ></paginate>
      </div>
    </div>
  </div>
</template>

<script>
import swal from 'sweetalert';
import Paginate from 'vuejs-paginate';
import { lightFormat } from 'date-fns';
import api from '../../../../services/api';

export default {
  props: ['id'],

  components: {
    Paginate,
  },

  methods: {
    async groupFilter(groupSelected) {
      const response = await api.get(`/action-permissions/${this.id}`, {
        headers: {
          'x-access-token': this.userAuth.token,
        },
      });

      if (groupSelected !== 'all') {
        this.actionItems = [];
        response.data.actions.filter((el) => {
          if (el.groupIdList.length > 0) {
            for (let j = 0; j < el.groupIdList.length; j += 1) {
              if (el.groupIdList[j].name === groupSelected) {
                this.actionItems.push({
                  controlId: el.controlId,
                  name: el.name,
                  groupId: el.groupIdList,
                  createdAt: el.createdAt,
                  channel: el.channel,
                  status: {
                    active: this.checkStatus(el._id),
                    id: el._id,
                  },
                });
              }
            }
          }
        });
      } else {
        this.getActions();
      }
    },

    checkStatus(id) {
      if (this.actionPermissions.length < 1) {
        return true;
      } else if (this.actionPermissions.includes(id)) {
        return true;
      }
      return false;
    },

    async getActions() {
      this.loading = true;

      const { data } = await api.get(`/action-permissions/${this.id}`, {
        headers: {
          'x-access-token': this.userAuth.token,
        },
        params: {
          page: this.page,
          limit: this.limit,
          filter: this.filter,
        },
      });

      this.total = data.total;

      this.actionItems = [];
      this.actionPermissions = [];
      this.actionPermissions = data.actionPermissions;

      data.actions.map((action) => {
        this.actionItems.push({
          controlId: action.controlId,
          name: action.name,
          groupId: action.groupIdList,
          createdAt: action.createdAt,
          channel: action.channel,
          status: { active: this.checkStatus(action._id), id: action._id },
        });
      });

      this.loading = false;
    },

    async editActionStatus(actionData) {
      const actionStatus = {};

      if (actionData.active === true) {
        actionStatus.id_allowed = actionData.id;
      } else {
        actionStatus.id_not_allowed = actionData.id;
      }

      try {
        await api.put(
          `/action-permissions/${this.id}`,
          { ...actionStatus, updatedBy: this.userAuth._id },
          {
            headers: {
              'x-access-token': this.userAuth.token,
            },
          },
        );
      } catch (error) {
        actionData.active = !actionData.active;
        swal(
          this.$t('genericMessages.error.title'),
          error.response.data.message,
          'error',
        );
      }
    },

    async editAllActions(value) {
      try {
        await api.put(
          `/action-permissions/all/${this.id}`,
          { selectAll: value, updatedBy: this.userAuth._id },
          {
            headers: {
              'x-access-token': this.userAuth.token,
            },
          },
        );

        swal(
          this.$t('genericMessages.success.title'),
          this.$t('genericMessages.success.description'),
          'success',
        );

        await this.getActions();
      } catch (error) {
        swal(
          this.$t('genericMessages.error.title'),
          this.$t('genericMessages.error.description'),
          'error',
        );
      }
    },

    formatDate(actionDate) {
      return lightFormat(new Date(actionDate), 'dd/MM/yyyy');
    },

    handleFilter() {
      if (this.filter !== null && this.filter !== '') {
        clearTimeout(this.typingTimer);
        this.typingTimer = setTimeout(this.getActions, 600);
      }

      if (this.filter === '') {
        this.filter = null;
        this.page = 1;
        this.getActions();
      }
    },

    cleanFilter() {
      this.filter = null;
      this.getActions();
    },
  },

  data: () => ({
    loading: false,
    userAuth: null,
    filter: null,
    page: 1,
    limit: 10,
    total: 1,
    actionFields: [],
    actionPermissions: null,
    actionItems: [],
    groupSelected: null,
    optionsGroups: [
      { value: null, text: 'Selecione o grupo de ação' },
      { value: 'all', text: 'Todos' },
    ],
  }),

  async created() {
    this.userAuth = JSON.parse(localStorage.getItem('_indecx'));

    const response = await api.get(`action-groups/${this.userAuth.companyId}`, {
      headers: { 'x-access-token': this.userAuth.token },
      params: {
        page: this.page,
        limit: this.limit,
        filter: this.filter ? this.filter : null,
      },
    });

    response.data.groups.forEach((el) => {
      this.optionsGroups.push({
        value: el.name,
        text: el.name,
      });
    });

    this.getActions();

    this.actionFields = [
      {
        key: 'controlId',
        label: this.$t('menu.settings.users.labels.controlId'),
      },
      { key: 'name', label: this.$t('menu.settings.users.labels.actions') },
      {
        key: 'groupId',
        label: this.$t('menu.settings.users.labels.groupAction'),
      },
      {
        key: 'createdAt',
        label: this.$t('menu.settings.users.labels.creationDate'),
      },
      { key: 'channel', label: this.$t('menu.settings.users.labels.channel') },
      { key: 'status', label: this.$t('menu.settings.users.labels.status') },
    ];
  },
};
</script>

<style lang="scss" scoped>
@import '@/themes/default/variables.scss';

.header-icon {
  float: left;
  font-size: 12px;
  margin-top: 4px;
  padding-right: 12px;
  color: $gray;
}

h2 {
  text-transform: uppercase;
  font-size: 21px;
  font-weight: 100;
  color: $gray;
}

.header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  .search {
    display: flex;
    flex-direction: row;
    min-width: 40%;

    input {
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
    }

    button {
      border-bottom-left-radius: 0;
      border-top-left-radius: 0;
    }
  }
}

.pagination-area-answers >>> a {
  position: relative;
  display: block;
  padding: 10px;
  margin-left: -1px;
  line-height: 1.25;
  color: #4ce595;
  background-color: #fff;
  border: 1px solid #dee2e6;
  outline: none;
}

.pagination-area-answers >>> .active a {
  text-decoration: none;
  z-index: 1;
  color: #fff;
  background: #4ce595;
  border-color: #4ce595;
  outline: none;
}

.pagination-area-answers a {
  outline: none;
}
</style>



// WEBPACK FOOTER //
// src/components/molecules/modal-permissions/action-hierarchy/index.vue